<template>
  <div class="kent-staff-doc">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">
          {{ reportBrandLabel }}
          <span class="text-primary">| Employment Form</span>
        </h4>
        <b-badge v-show="!loading" variant="light-primary" class="ml-2">
          {{ reportItems.length }} Forms
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem;" />
      <p class="mt-3 font-weight-bold">Loading employment forms...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <div v-else class="documents-wrap">
      <div
        v-for="(staff, index) in reportItems"
        :key="staff.id || index"
        class="form-sheet"
      >
        <header class="form-head">
          <div class="form-head__brand">
            <img
              :src="schoolLogo"
              alt="Kent Schooling System"
              class="form-head__logo"
            />
            <div class="form-head__text">
              <h2 class="form-head__school">{{ schoolName }}</h2>
              <p v-if="campusName" class="form-head__campus">{{ campusName }}</p>
              <p v-if="campusAddress" class="form-head__address">
                {{ campusAddress }}
              </p>
            </div>
          </div>

          <div class="form-head__photo">
            <img
              v-if="hasStaffPhoto(staff)"
              :src="staffPhotoUrl(staff)"
              alt="Staff photo"
              class="form-head__photo-img"
              @error="onPhotoError($event, staff)"
            />
            <div v-else class="form-head__photo-placeholder">
              <span>Photo</span>
            </div>
          </div>
        </header>

        <h1 class="form-title">
          <span class="form-title__text">Employment Form</span>
        </h1>

        <div class="id-strip">
          <div class="id-strip__item">
            <span class="id-strip__label">Staff ID :</span>
            <span class="id-strip__value">{{ staff.id || "—" }}</span>
          </div>
          <div class="id-strip__divider"></div>
          <div class="id-strip__item">
            <span class="id-strip__label">RF ID :</span>
            <span class="id-strip__value">{{
              staffField(staff, ["rfID", "rfid", "RFID"])
            }}</span>
          </div>
        </div>

        <div class="form-content">
        <section class="form-section">
          <h3 class="form-section__title">
            <span class="form-section__accent"></span>
            Personal Information
          </h3>
          <div class="field-grid">
            <div class="field-item field-item--full">
              <span class="field-label">Full Name</span>
              <span class="field-value field-value--name">{{
                staffDisplayName(staff)
              }}</span>
            </div>
            <div class="field-item field-item--full">
              <span class="field-label">Father Name</span>
              <span class="field-value">{{ staffFatherName(staff) }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">CNIC</span>
              <span class="field-value">{{
                staffField(staff, ["cnic", "CNIC", "nic"])
              }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">Gender</span>
              <span class="field-value">{{
                staffField(staff, ["gender", "sex"])
              }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">Date of Birth</span>
              <span class="field-value">{{
                formatKentDateShort(staffDateOfBirth(staff))
              }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">Contact No.</span>
              <span class="field-value">{{
                staffField(staff, [
                  "contact_number",
                  "mobile",
                  "phone",
                  "contact",
                ])
              }}</span>
            </div>
          </div>
        </section>

        <section class="form-section">
          <h3 class="form-section__title">
            <span class="form-section__accent"></span>
            Employment Details
          </h3>
          <div class="field-grid">
            <div class="field-item">
              <span class="field-label">Designation</span>
              <span class="field-value">{{ staffDesignation(staff) }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">Department</span>
              <span class="field-value">{{
                staffDepartment(staff, departments)
              }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">Date of Appointment</span>
              <span class="field-value">{{
                formatKentDateShort(staffAppointedOn(staff))
              }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">Status</span>
              <span class="field-value field-value--cap">{{
                staffField(staff, ["status"])
              }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">Qualification</span>
              <span class="field-value">{{
                staffField(staff, ["qualification", "education"])
              }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">Salary</span>
              <span class="field-value field-value--salary">{{
                staffSalaryLabel(staff)
              }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">Experience</span>
              <span class="field-value">{{
                staffField(staff, ["experience", "totalExperience"])
              }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">Email</span>
              <span class="field-value">{{ staffEmail(staff) || "—" }}</span>
            </div>
            <div class="field-item field-item--full">
              <span class="field-label">Address</span>
              <span class="field-value">{{ staffAddress(staff) || "—" }}</span>
            </div>
          </div>
        </section>

        <section class="form-section">
          <h3 class="form-section__title">
            <span class="form-section__accent"></span>
            Bank Details
          </h3>
          <div class="field-grid">
            <div class="field-item">
              <span class="field-label">Pay Mode</span>
              <span class="field-value">{{
                staffField(staff, ["payMode", "pay_mode", "paymentMode"])
              }}</span>
            </div>
            <div class="field-item">
              <span class="field-label">Account Title</span>
              <span class="field-value">{{
                staffField(staff, [
                  "accountTitle",
                  "account_title",
                  "staffAccountTitle",
                ])
              }}</span>
            </div>
            <div class="field-item field-item--full">
              <span class="field-label">Account Number</span>
              <span class="field-value">{{
                staffField(staff, [
                  "accountNumber",
                  "account_number",
                  "staffAccountNumber",
                  "accountNo",
                ])
              }}</span>
            </div>
          </div>
        </section>
        </div>

        <div class="form-sheet-spacer" aria-hidden="true"></div>

        <footer class="form-signatures">
          <div class="sign-block">
            <div class="sign-block__space"></div>
            <div class="sign-block__line"></div>
            <p class="sign-block__label">Teacher Signature</p>
          </div>
          <div class="sign-block">
            <div class="sign-block__space"></div>
            <div class="sign-block__line"></div>
            <p class="sign-block__label">Principal Signature</p>
          </div>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";
import {
  getMediaPreviewUrl,
  handleDrivePreviewError,
} from "@/utils/gdriveUpload";
import {
  parseStaffIds,
  parseKentDepIds,
  loadKentCampusInfo,
  loadKentStaffRecords,
  loadKentStaffByDepartments,
  staffDisplayName,
  staffFatherName,
  staffDesignation,
  staffDepartment,
  formatKentDateShort,
  staffDateOfBirth,
  staffEmail,
  staffAddress,
  staffField,
  staffSalary,
  formatKentCurrency,
  isStaffLetterPrintDomain,
  defaultSchoolNameForDomain,
} from "./kentStaffPrintUtils";

export default {
  name: "KentTeachersFormPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      departments: [],
      schoolName: defaultSchoolNameForDomain(
        (this.$store.state.report || "").replace(/_/g, "")
      ),
      campusName: "",
      campusAddress: "",
      schoolLogo: "/kennt_logo.png",
      db: "",
      cID: 0,
      staffIDs: [],
      depIDs: [],
      staffStatus: "present",
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
      defaultPlaceholder:
        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23e8ecf0'/%3E%3Ccircle cx='50' cy='38' r='20' fill='%23adb5bd'/%3E%3Cpath d='M10 100 Q10 68 50 68 Q90 68 90 100Z' fill='%23adb5bd'/%3E%3C/svg%3E",
    };
  },
  computed: {
    reportBrandLabel() {
      return this.schoolName || defaultSchoolNameForDomain(this.reportDomain);
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    staffDisplayName,
    staffFatherName,
    staffDesignation,
    staffDepartment,
    formatKentDateShort,
    staffDateOfBirth,
    staffEmail,
    staffAddress,
    staffField,
    staffSalaryLabel(staff) {
      const salary = staffSalary(staff);
      if (salary === null) return "—";
      return `PKR ${formatKentCurrency(salary)}`;
    },
    initReport() {
      if (!isStaffLetterPrintDomain(this.reportDomain)) {
        this.error = "This report is not available for this school.";
        return;
      }

      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = Number(query.cID || this.$store.state.userData.cId || 0);
      this.staffIDs = parseStaffIds(query);
      this.depIDs = parseKentDepIds(query);
      this.staffStatus = query.status || "present";

      if (!this.db) {
        this.error = "Database identifier is missing.";
        return;
      }
      if (!this.staffIDs.length && !this.depIDs.length) {
        this.error = "No staff or department selected for employment forms.";
        return;
      }

      this.loadReportData();
    },
    staffAppointedOn(staff) {
      return (
        staff?.appointedOn ||
        staff?.appointmentDate ||
        staff?.joiningDate ||
        null
      );
    },
    hasStaffPhoto(staff) {
      const url = staff?.picture || staff?.photo || staff?.image || "";
      return !!url;
    },
    staffPhotoUrl(staff) {
      const url = staff?.picture || staff?.photo || staff?.image || "";
      if (!url) return this.defaultPlaceholder;
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },
    onPhotoError(event, staff) {
      const url = staff?.picture || staff?.photo || staff?.image || "";
      if (url && !String(url).startsWith("blob:")) {
        handleDrivePreviewError(event, url, "image");
        return;
      }
      event.target.src = this.defaultPlaceholder;
    },
    async loadDepartments() {
      try {
        const response = await axios.get(
          `${this.$store.state.domain}StaffDepartments`,
          {
            params: { db: this.db, cId: this.cID },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData?.token}`,
              accept: "application/json",
            },
          }
        );
        this.departments = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
          ? response.data.data
          : [];
      } catch (e) {
        console.warn("Departments failed to load", e);
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const campus = await loadKentCampusInfo(this, this.db, this.cID);
        this.schoolName = campus.schoolName;
        this.campusName = campus.campusName;
        this.campusAddress = campus.campusAddress;
        this.schoolLogo = campus.schoolLogo;

        await this.loadDepartments();
        if (this.staffIDs.length) {
          this.reportItems = await loadKentStaffRecords(
            this,
            this.db,
            this.cID,
            this.staffIDs
          );
        } else {
          this.reportItems = await loadKentStaffByDepartments(
            this,
            this.db,
            this.cID,
            this.depIDs,
            this.staffStatus
          );
        }

        if (!this.reportItems.length) {
          this.error = "No staff records found for the selected staff.";
        }
      } catch (err) {
        console.error("Kent employment form error:", err);
        this.error = "Failed to load staff records for employment forms.";
      } finally {
        this.loading = false;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");

$kent-navy: #1a2b56;
$kent-navy-light: #2d4a7a;
$kent-gold: #c9a227;
$text-dark: #1f2937;
$text-muted: #6b7280;
$border-light: #e5e7eb;
$font-main: "Inter", "Segoe UI", sans-serif;

.kent-staff-doc {
  background: #eef1f6;
  min-height: 100vh;
  padding: 2rem;
  font-family: $font-main;
  color: $text-dark;
}

.control-bar {
  background: #fff;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 10;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn-modern {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  font-weight: 700;
  cursor: pointer;
}

.btn-print {
  background: linear-gradient(135deg, $kent-navy 0%, $kent-navy-light 100%);
  color: #fff;
}

.btn-close {
  background: #fff;
  border: 1px solid #dfe6e9;
  color: #636e72;
}

.documents-wrap {
  max-width: 820px;
  margin: 0 auto;
}

.form-sheet {
  background: #fff;
  padding: 1.75rem 2rem 1.5rem;
  margin-bottom: 2rem;
  border-radius: 6px;
  box-shadow: 0 4px 24px rgba(26, 43, 86, 0.1);
  box-sizing: border-box;
}

/* ─── Header ─── */
.form-head {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1.25rem;
  align-items: start;
  margin-bottom: 1rem;
}

.form-head__brand {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  min-width: 0;
}

.form-head__logo {
  width: 68px;
  height: auto;
  object-fit: contain;
  flex-shrink: 0;
}

.form-head__school {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: $kent-navy;
  line-height: 1.25;
}

.form-head__campus {
  margin: 0.2rem 0 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: $kent-navy-light;
}

.form-head__address {
  margin: 0.12rem 0 0;
  font-size: 0.78rem;
  color: $text-muted;
  line-height: 1.4;
}

.form-head__photo {
  width: 35mm;
  height: 45mm;
  border: 2px solid $kent-navy;
  border-radius: 4px;
  overflow: hidden;
  background: #f8fafc;
  box-shadow: inset 0 0 0 3px #fff, 0 0 0 1px $kent-gold;
  flex-shrink: 0;
}

.form-head__photo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.form-head__photo-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 700;
  color: $text-muted;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* ─── Title ─── */
.form-title {
  text-align: center;
  margin: 0 0 0.85rem;

  &__text {
    display: inline-block;
    font-size: 1.05rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #fff;
    background: $kent-navy;
    padding: 0.45rem 1.75rem;
    border-radius: 2px;
    box-shadow: 0 2px 8px rgba(26, 43, 86, 0.18);
  }
}

/* ─── ID strip ─── */
.id-strip {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(26, 43, 86, 0.05);
  border: 1px solid rgba(26, 43, 86, 0.12);
  border-left: 4px solid $kent-gold;
  border-radius: 4px;
  padding: 0.55rem 0.85rem;
  margin-bottom: 1rem;
}

.id-strip__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.id-strip__divider {
  width: 1px;
  height: 1.4rem;
  background: $border-light;
  flex-shrink: 0;
}

.id-strip__label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: $text-muted;
}

.id-strip__value {
  font-size: 0.92rem;
  font-weight: 700;
  color: $kent-navy;
}

/* ─── Sections ─── */
.form-section {
  margin-bottom: 0.85rem;

  &:last-of-type {
    margin-bottom: 1.1rem;
  }
}

.form-section__title {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0 0 0.55rem;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: $kent-navy;
}

.form-section__accent {
  display: inline-block;
  width: 4px;
  height: 14px;
  background: $kent-gold;
  border-radius: 1px;
  flex-shrink: 0;
}

.field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  border: 1px solid $border-light;
  border-radius: 4px;
  overflow: hidden;
  background: #fafbfc;
}

.field-item {
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
  padding: 0.48rem 0.75rem;
  border-bottom: 1px solid $border-light;
  border-right: 1px solid $border-light;

  &:nth-child(2n) {
    border-right: none;
  }

  &--full {
    grid-column: 1 / -1;
    border-right: none;
  }

  &:last-child,
  &:nth-last-child(2):nth-child(odd) {
    border-bottom: none;
  }
}

.field-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: $text-muted;
}

.field-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: $text-dark;
  line-height: 1.4;
  word-break: break-word;

  &--name {
    text-transform: uppercase;
    font-weight: 700;
    color: $kent-navy;
  }

  &--cap {
    text-transform: capitalize;
  }

  &--salary {
    font-weight: 700;
    color: $kent-navy;
  }
}

.form-sheet-spacer {
  display: none;
}

/* ─── Signatures ─── */
.form-signatures {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.5rem;
  margin-top: 1.25rem;
  padding-top: 0.5rem;
}

.sign-block {
  display: grid;
  grid-template-rows: auto auto 1fr;
}

.sign-block__space {
  height: 2.5rem;
  min-height: 2.5rem;
}

.sign-block__line {
  border-top: 1px solid $text-dark;
}

.sign-block__label {
  margin: 0.3rem 0 0;
  font-size: 0.88rem;
  font-weight: 700;
  color: $text-dark;
  text-align: center;
}

@media print {
  .no-print {
    display: none !important;
  }

  .kent-staff-doc {
    background: #fff;
    padding: 0;
    min-height: 0 !important;
    height: auto !important;
  }

  .documents-wrap {
    max-width: none;
    margin: 0;
    padding: 0;
  }

  .form-sheet {
    display: flex !important;
    flex-direction: column !important;
    box-shadow: none;
    border-radius: 0;
    margin: 0;
    padding: 0;
    width: 100%;
    min-height: 281mm !important;
    height: auto !important;
    font-size: 12pt;
    font-weight: 600;
    line-height: 1.55;
    color: #000;
    overflow: visible !important;
    page-break-inside: avoid;
    break-inside: avoid;
    page-break-after: avoid;
    break-after: avoid;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .form-sheet + .form-sheet {
    page-break-before: always;
    break-before: page;
  }

  .form-head {
    margin-bottom: 0.85rem;
    flex-shrink: 0;
  }

  .form-head__logo {
    width: 76px;
  }

  .form-head__school {
    font-size: 16pt;
    font-weight: 700;
    color: #000;
  }

  .form-head__campus {
    font-size: 12pt;
    font-weight: 700;
    color: #000;
  }

  .form-head__address {
    font-size: 11pt;
    font-weight: 600;
    color: #000;
  }

  .form-head__photo {
    width: 35mm !important;
    height: 45mm !important;
    border: 2pt solid #000 !important;
    box-shadow: none !important;
  }

  .form-title {
    margin-bottom: 0.75rem;
    flex-shrink: 0;

    &__text {
      font-size: 14pt;
      font-weight: 700;
      padding: 0.5rem 2rem;
    }
  }

  .id-strip {
    margin-bottom: 0.85rem;
    padding: 0.55rem 0.85rem;
    border: 1.5pt solid #000 !important;
    border-left: 4pt solid $kent-gold !important;
    background: #f3f4f6 !important;
    flex-shrink: 0;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .id-strip__divider {
    background: #000 !important;
    width: 1.5pt;
  }

  .id-strip__label {
    font-size: 10pt;
    font-weight: 700;
    color: #000;
  }

  .id-strip__value {
    font-size: 12pt;
    font-weight: 700;
    color: #000;
  }

  .form-content {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    overflow: visible !important;
  }

  .form-section {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    margin-bottom: 0;
    overflow: visible !important;
  }

  .form-sheet-spacer {
    display: block !important;
    flex: 1 1 auto !important;
    min-height: 4mm;
  }

  .form-section__title {
    font-size: 12pt;
    font-weight: 700;
    margin-bottom: 0.55rem;
    flex-shrink: 0;
    color: #000;
  }

  .field-grid {
    flex: 0 0 auto;
    overflow: visible !important;
    border: 2pt solid #000 !important;
    background: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .field-item {
    padding: 0.65rem 0.85rem;
    min-height: auto;
    overflow: visible !important;
    border-bottom: 1.5pt solid #000 !important;
    border-right: 1.5pt solid #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    &:last-child,
    &:nth-last-child(2):nth-child(odd) {
      border-bottom: 1.5pt solid #000 !important;
    }

    &:nth-child(2n) {
      border-right: none !important;
    }

    &--full {
      border-right: none !important;
    }
  }

  .field-label {
    font-size: 10pt;
    font-weight: 700;
    color: #000;
  }

  .field-value {
    font-size: 12pt;
    font-weight: 700;
    line-height: 1.5;
    color: #000;
    overflow: visible !important;
    white-space: normal;
    word-break: break-word;

    &--name,
    &--salary {
      color: #000;
    }
  }

  .form-signatures {
    gap: 2.5rem;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    padding-top: 0;
    padding-bottom: 0;
    flex-shrink: 0;
    align-items: end;
  }

  .sign-block__space {
    height: 5rem;
    min-height: 5rem;
  }

  .sign-block__line {
    border-top: 1.5pt solid #000 !important;
  }

  .sign-block__label {
    font-size: 12pt;
    font-weight: 700;
    margin-top: 0.4rem;
    color: #000;
  }

  .form-title__text,
  .form-section__accent,
  .id-strip,
  .field-grid,
  .field-item,
  .form-head__photo {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>

<style lang="scss">
@page {
  size: A4 portrait;
  margin: 8mm;
}

@media print {
  html,
  body {
    width: 100% !important;
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    overflow: visible !important;
  }

  #app,
  .content-wrapper,
  .content-area-wrapper,
  .container,
  .router-view,
  .layout-full {
    min-height: 0 !important;
    height: auto !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
  }

  .form-sheet {
    display: flex !important;
    flex-direction: column !important;
    min-height: 281mm !important;
    height: auto !important;
    overflow: visible !important;
  }

  .form-sheet-spacer {
    flex: 1 1 auto !important;
  }

  .field-grid,
  .field-item,
  .field-value {
    overflow: visible !important;
  }
}
</style>
